import { Injectable } from '@angular/core';
import { IpoFetchService } from '@ip-only/front-end-library';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Duct,
  DuctData,
  DuctInfo,
  DuctMapping,
  DuctProperties,
  SubDuct,
} from '../../../span-progress-entities';
import { DuctApiService } from '../../api/duct-api.service';

const mapToDuctProperties = (
  { id, colorCode, name, serialNumber, number, colorName, ductName }: DuctInfo,
  fromPointName: string,
  toPointName: string,
) => ({
  id,
  colorCode,
  name,
  serialNumber,
  number,
  colorName,
  ductName,
  fromPointName,
  toPointName,
});

@Injectable({ providedIn: 'root' })
export class DuctService extends IpoFetchService<DuctData, DuctApiService> {
  constructor(apiService: DuctApiService) {
    super(apiService);
  }

  getDuctList$(): Observable<DuctMapping[]> {
    return this.entity$.pipe(
      map((ductResponse) => {
        return ductResponse ? this.createListObjects(ductResponse.ductMap) : [];
      }),
    );
  }

  getMultiductProperties$(ductId: string) {
    return this.entity$.pipe(
      map((ductData) => {
        if (!ductData) {
          return null;
        }
        const multiduct = Object.values(ductData.ductMap).filter(
          (duct: Duct) => duct.id === ductId,
        );
        if (!multiduct.length) {
          return null;
        }
        return mapToDuctProperties(multiduct[0], '', '');
      }),
    );
  }

  getDuctProperties$(
    ductId: string,
    subDuctId: string,
    toPointId?: string,
  ): Observable<DuctProperties | null> {
    return this.entity$.pipe(
      map((ductData) => {
        if (!ductData) {
          return null;
        }
        const ductCandidates = Object.values(ductData.ductMap).filter(
          (duct: Duct) => duct.id === ductId,
        );
        const ductToPointId = toPointId
          ? this.findDuctToPointId(ductCandidates, toPointId)
          : null;
        return (
          ductToPointId ?? this.findSubDuctMatch(ductCandidates, subDuctId)
        );
      }),
    );
  }

  getSpanDuctData$(spanId: string): Observable<Duct[]> {
    return this.entity$.pipe(
      map((ductData) => {
        if (!ductData || !ductData.spanIdToDuctIdMap[spanId]) {
          return [];
        }
        return ductData.spanIdToDuctIdMap[spanId].map(
          (id) => ductData.ductMap[id],
        );
      }),
    );
  }

  clearData() {
    this.entity$.next(null);
  }

  private findDuctToPointId(
    ductCandidates: Duct[],
    toPointId: string,
  ): DuctProperties | null {
    const ductMatch = ductCandidates.find(
      (duct) => duct.toPointId === toPointId,
    );
    if (!ductMatch) {
      return null;
    }
    return mapToDuctProperties(
      ductMatch,
      ductMatch.fromPointName,
      ductMatch.toPointName,
    );
  }

  private findSubDuctMatch(ductCandidates: Duct[], subDuctId: string) {
    const ductMatch = ductCandidates.find((duct) =>
      duct.subDucts.some((subDuct) => subDuct.id === subDuctId),
    );
    if (!ductMatch) {
      return null;
    }
    const subDuctMatch = ductMatch.subDucts.find(
      (subDuct) => subDuct.id === subDuctId,
    ) as SubDuct;
    return mapToDuctProperties(
      subDuctMatch,
      ductMatch.fromPointName,
      ductMatch.toPointName,
    );
  }

  private createListObjects(ductMap: { [key: string]: Duct }): DuctMapping[] {
    return Object.keys(ductMap).map(
      (key) =>
        ({
          ductMapId: key,
          ...ductMap[key],
        }) as DuctMapping,
    );
  }
}
